import * as uuid from 'uuid';

import type { TrackPropertiesDTO } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import {
  PositionRequest,
  TrackApiWebClient,
} from '@nora-monorepo/track-api-web-client';
import {
  useAuthStore,
  useConfigStore,
  useGenericUserStore,
} from '@nora-monorepo/ui-shared';

import { useMapStore } from '#nora-ui/map/stores/map-store';

type SimulatePositionParams = Partial<Pick<PositionRequest, 'latitude' | 'longitude' | 'locationAccuracy' | 'heading' | 'speed'>> & {
  sidc?: string;
};

export async function simulatePosition({ latitude, longitude, locationAccuracy, heading, speed, sidc }: SimulatePositionParams = {}) {
  const mapStore = useMapStore();
  const configStore = useConfigStore();
  const userStore = useGenericUserStore();
  const authStore = useAuthStore();

  const maps = Object.values(mapStore.mapInstances);
  const user = userStore.user;
  const grpcServerUrl = configStore.get('trackApi.webUrl');
  const deviceId = localStorage.getItem('deviceId') ?? 'unknown-device';

  if (!user) {
    console.error('User not found in store.');

    return;
  }

  if (!isNonEmptyString(grpcServerUrl)) {
    console.error('gRPC server URL not found in config store.');

    return;
  }

  if (maps.length === 0) {
    console.error('No map instance found.');

    return;
  }

  const client = new TrackApiWebClient(grpcServerUrl);

  const firstMap = maps[0];
  const map = mapStore.getMapByIdOrThrow(firstMap.id);
  const location = {
    latitude,
    longitude,
  };

  if (location.latitude === undefined || location.longitude === undefined) {
    const center = map.getCenter();

    location.latitude = center.latitude;
    location.longitude = center.longitude;
  }

  const newHeading = heading ?? Math.random() * 360;
  const properties: TrackPropertiesDTO = {
    deviceId,
    type: 'user',
  };

  if (isNonEmptyString(sidc)) {
    properties.sidc = sidc;
  }

  const position = PositionRequest.create({
    positionId: uuid.v4(),
    producerId: user.id,
    latitude: location.latitude,
    longitude: location.longitude,
    locationAccuracy: locationAccuracy ?? 0,
    heading: newHeading,
    speed: speed ?? 0,
    clientTimestamp: new Date().toISOString(),
    properties: properties as Record<string, string>,
  });

  await client.trackPosition(
    position,
    {
      meta: {
        authorization: `Bearer ${authStore.token}`,
      },
    },
  );
}

export async function simulateAccuracyImprovement({ latitude, longitude, locationAccuracy }: SimulatePositionParams = {}) {
  let accuracy = locationAccuracy ?? 500;
  const targetAccuracy = 5;
  const steps = 10;
  const accuracyStep = (accuracy - targetAccuracy) / steps;

  for (let i = 0; i < steps; i++) {
    await simulatePosition({ latitude, longitude, locationAccuracy: Math.max(accuracy, targetAccuracy) });
    accuracy -= accuracyStep;

    await new Promise(resolve => setTimeout(resolve, 250));
  }
}

export async function simulateHeadingChange({ latitude, longitude, heading }: SimulatePositionParams = {}) {
  let currentHeading = heading ?? 0;
  const steps = 3600;
  const headingStep = 3600 / steps;

  for (let i = 0; i < steps; i++) {
    await simulatePosition({ latitude, longitude, heading: currentHeading });

    currentHeading += headingStep;

    if (currentHeading >= 360) {
      currentHeading -= 360;
    }

    await new Promise(resolve => setTimeout(resolve, 10));
  }
}
